import React, {useEffect, useRef, useMemo} from "react";
import * as d3 from "d3";
import "./CenterPlot.css";

interface DataPoint {
    x: number;
    y: number;
    source: string;
    filename: string;
    imageUrl: string;
    neighbors?: DataPoint[];
}

interface CenterPlotProps {
    data: DataPoint[];
    setSelectedImage: (image: DataPoint) => void;
    filterImage: string;
    hideAPNs: boolean;
    fetchError: boolean;
}

const CenterPlot: React.FC<CenterPlotProps> = ({data, setSelectedImage, filterImage, hideAPNs, fetchError}) => {
    const svgRef = useRef<SVGSVGElement>(null);

    const filteredData = useMemo(() => {
        if (!filterImage) return data;
        if (hideAPNs) {
            if (!filterImage) return [];
            const selected = data.find(d => d.filename === filterImage);
            const filteredNeighbors = selected?.neighbors?.filter(d => d.source !== "apn") || [];
            if (!selected) return [];
            return [selected, ...(filteredNeighbors || [])];
        }

        const selected = data.find(d => d.filename === filterImage);
        if (!selected) return [];
        return [selected, ...(selected.neighbors || [])];
    }, [data, filterImage, hideAPNs]);

    useEffect(() => {
        const svg = d3.select(svgRef.current);
        svg.selectAll("*").remove();

        const width = (svgRef.current?.clientWidth || 400) - 40;
        const height = (svgRef.current?.clientHeight || 300) - 40;
        const margin = 20;

        const xScale = d3
            .scaleLinear()
            .domain(d3.extent(filteredData, (d) => d.x) as [number, number])
            .range([margin, width - margin]);

        const yScale = d3
            .scaleLinear()
            .domain(d3.extent(filteredData, (d) => d.y) as [number, number])
            .range([height - margin, margin]);

        const zoomGroup = svg.append("g").attr("class", "zoom-group");

        const points = zoomGroup
            .selectAll("g.point")
            .data(filteredData)
            .enter()
            .append("g")
            .attr("class", "point")
            .attr("transform", (d) => `translate(${xScale(d.x)},${yScale(d.y)})`)
            .on("click", (event, d) => setSelectedImage(d));

        // points
        //     .append("image")
        //     .attr("xlink:href", (d) => d.imageUrl)
        //     .attr("x", -15) // Center the image
        //     .attr("y", -15) // Center the image
        //     .attr("width", 30)
        //     .attr("height", 30)
        //     .attr("cursor", "pointer")
        //     // .classed("apn-border", (d) => d.source === "apn");

        points.append("foreignObject")
            .attr("x", -15)
            .attr("y", -15)
            .attr("width", 30)
            .attr("height", 30)
            .append("xhtml:img")
            .attr("src", (d) => d.imageUrl)
            .attr("width", 30)
            .attr("height", 30)
            .attr("cursor", "pointer");

        points
            .append("title")
            .text((d) => `Filename: ${d.filename}`);

        // Define zoom behavior
        const zoom = d3.zoom<SVGSVGElement, unknown>().on("zoom", (event) => {
            zoomGroup.attr("transform", event.transform);
        });

        svg.call(zoom as any);
    }, [filteredData, setSelectedImage]);

    if (fetchError) {
        return <div className="fetch-error">Error fetching data. Please try again later.</div>;
    }

    return <svg ref={svgRef} width="100%" height="100%"></svg>;
};

export default CenterPlot;